import AddIcon from '@mui/icons-material/Add';
import BugReportIcon from '@mui/icons-material/BugReport';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type {
  EvalCompiledRuntime,
  EvalFixtureSet,
  FixtureAuthoringConfig,
  FixtureShape,
  FixtureTranscriptItem,
} from '../api/evalApi';
import EvalFixtureTranscriptDropzone, { inferTranscriptId } from './EvalFixtureTranscriptDropzone';

type FixtureDraft = {
  name: string;
  description: string;
  authoringConfig: FixtureAuthoringConfig;
};

type SaveIntent = 'stay' | 'use_in_case';

type FlashMessage = {
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
};

type Props = {
  mode: 'create' | 'version';
  initialFixture?: EvalFixtureSet;
  existingRuntimePreview: EvalCompiledRuntime | null;
  isSubmitting: boolean;
  caseFlowActionLabel?: string | null;
  onSubmit: (draft: FixtureDraft, intent: SaveIntent) => Promise<void>;
};

export const FIXTURE_TEXTAREA_MAX_ROWS = {
  description: 4,
  draftTranscriptBody: 12,
  transcriptBody: 10,
  exampleNotes: 6,
  templateVariables: 8,
  anonymizationNotes: 4,
  compiledRuntimeJSON: 16,
} as const;

function defaultAuthoringConfig(): FixtureAuthoringConfig {
  return {
    kind: 'anonymized_transcript_bundle',
    fixture_shape: 'single_transcript',
    example_context: {},
    identity_defaults: {},
    template_variables: {},
    anonymization: {
      status: 'manual_reviewed',
      notes: '',
    },
    transcripts: [],
  };
}

function inferFixtureShape(config?: FixtureAuthoringConfig | null): FixtureShape {
  if (config?.fixture_shape) {
    return config.fixture_shape;
  }
  return (config?.transcripts?.length ?? 0) > 1 ? 'transcript_bundle' : 'single_transcript';
}

function cloneAuthoringConfig(config?: FixtureAuthoringConfig | null): FixtureAuthoringConfig {
  if (!config) {
    return defaultAuthoringConfig();
  }
  return {
    kind: 'anonymized_transcript_bundle',
    fixture_shape: inferFixtureShape(config),
    example_context: {
      display_label: config.example_context?.display_label ?? '',
      notes: config.example_context?.notes ?? '',
    },
    identity_defaults: { ...(config.identity_defaults ?? {}) },
    template_variables: { ...(config.template_variables ?? {}) },
    primary_transcript_id: config.primary_transcript_id ?? '',
    expose_primary_as_transcript: config.expose_primary_as_transcript ?? false,
    anonymization: {
      status: config.anonymization?.status ?? 'manual_reviewed',
      notes: config.anonymization?.notes ?? '',
    },
    transcripts: [...(config.transcripts ?? [])],
  };
}

function stringifyTemplateVariables(value?: Record<string, unknown>): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function transcriptBody(transcript: FixtureTranscriptItem): string {
  return transcript.markdown ?? transcript.content ?? '';
}

function uniqueTranscriptID(base: string, existingIDs: Set<string>): string {
  const trimmed = base.trim() || 'transcript';
  if (!existingIDs.has(trimmed)) {
    existingIDs.add(trimmed);
    return trimmed;
  }

  let suffix = 2;
  while (existingIDs.has(`${trimmed}-${suffix}`)) {
    suffix += 1;
  }
  const unique = `${trimmed}-${suffix}`;
  existingIDs.add(unique);
  return unique;
}

function normalizeAddedTranscripts(
  current: FixtureTranscriptItem[],
  added: FixtureTranscriptItem[]
): FixtureTranscriptItem[] {
  const seen = new Set(current.map((item) => item.id.trim()).filter(Boolean));
  return added.map((item, index) => {
    const base =
      inferTranscriptId(item.id || item.title || '') || `transcript-${current.length + index + 1}`;
    const id = uniqueTranscriptID(base, seen);
    return {
      ...item,
      id,
      title: item.title?.trim() || `Transcript ${current.length + index + 1}`,
    };
  });
}

function defaultPrimaryTranscriptID(
  transcripts: FixtureTranscriptItem[],
  requestedPrimaryTranscriptID?: string,
  exposePrimaryAsTranscript?: boolean
): string | undefined {
  const trimmed = requestedPrimaryTranscriptID?.trim();
  if (trimmed) {
    return trimmed;
  }
  if (transcripts.length === 1) {
    return transcripts[0]?.id.trim() || undefined;
  }
  if (exposePrimaryAsTranscript && transcripts.length > 0) {
    return transcripts[0]?.id.trim() || undefined;
  }
  return undefined;
}

function advancedInputSummary(authoringConfig: FixtureAuthoringConfig): string[] {
  const out: string[] = [];
  if (authoringConfig.identity_defaults?.person_id?.trim()) {
    out.push('person ID');
  }
  if (authoringConfig.identity_defaults?.org_id?.trim()) {
    out.push('org ID');
  }
  if (authoringConfig.identity_defaults?.draft_bundle_id?.trim()) {
    out.push('draft bundle ID');
  }
  if (Object.keys(authoringConfig.template_variables ?? {}).length > 0) {
    out.push('template variables');
  }
  if (authoringConfig.expose_primary_as_transcript) {
    out.push('compatibility transcript injection');
  }
  if (authoringConfig.anonymization.notes?.trim()) {
    out.push('anonymization notes');
  }
  return out;
}

function parsedTemplateVariables(templateVariablesText: string): {
  value: Record<string, unknown>;
  isValid: boolean;
} {
  try {
    return {
      value: JSON.parse(templateVariablesText || '{}') as Record<string, unknown>,
      isValid: true,
    };
  } catch {
    return { value: {}, isValid: false };
  }
}

function normalizedIdentityKeys(authoringConfig: FixtureAuthoringConfig): string[] {
  const keys: string[] = [];
  if (authoringConfig.identity_defaults?.person_id?.trim()) {
    keys.push('person_id');
  }
  if (authoringConfig.identity_defaults?.org_id?.trim()) {
    keys.push('org_id');
  }
  if (authoringConfig.identity_defaults?.draft_bundle_id?.trim()) {
    keys.push('draft_bundle_id');
  }
  return keys;
}

function draftRuntimeSummary(
  authoringConfig: FixtureAuthoringConfig,
  templateVariables: Record<string, unknown>
): EvalCompiledRuntime {
  const documentIDs = authoringConfig.transcripts
    .map((transcript) => transcript.id.trim())
    .filter(Boolean);
  const primaryTranscriptID = defaultPrimaryTranscriptID(
    authoringConfig.transcripts,
    authoringConfig.primary_transcript_id,
    authoringConfig.expose_primary_as_transcript
  );

  return {
    identity_context: {},
    document_context: {
      document_ids: documentIDs,
    },
    template_variables: templateVariables,
    summary: {
      transcript_count: documentIDs.length,
      document_ids: documentIDs,
      primary_transcript_id: primaryTranscriptID,
      injects_transcript: authoringConfig.expose_primary_as_transcript ?? false,
      identity_keys: normalizedIdentityKeys(authoringConfig),
      template_variable_keys: Object.keys(templateVariables),
    },
  };
}

export default function EvalFixtureEditor({
  mode,
  initialFixture,
  existingRuntimePreview,
  isSubmitting,
  caseFlowActionLabel,
  onSubmit,
}: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [authoringConfig, setAuthoringConfig] =
    useState<FixtureAuthoringConfig>(defaultAuthoringConfig);
  const [templateVariablesText, setTemplateVariablesText] = useState('{}');
  const [draftTranscriptTitle, setDraftTranscriptTitle] = useState('');
  const [draftTranscriptSessionDate, setDraftTranscriptSessionDate] = useState('');
  const [draftTranscriptBody, setDraftTranscriptBody] = useState('');
  const [draftTranscriptFormat, setDraftTranscriptFormat] = useState<'markdown' | 'text'>(
    'markdown'
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [flashMessage, setFlashMessage] = useState<FlashMessage | null>(null);

  useEffect(() => {
    setName(initialFixture?.name ?? '');
    setDescription(initialFixture?.description ?? '');
    const nextConfig = cloneAuthoringConfig(initialFixture?.authoring_config);
    setAuthoringConfig(nextConfig);
    setTemplateVariablesText(stringifyTemplateVariables(nextConfig.template_variables));
    setDraftTranscriptTitle('');
    setDraftTranscriptSessionDate('');
    setDraftTranscriptBody('');
    setDraftTranscriptFormat('markdown');
    setShowAdvancedOptions(false);
    setShowDebug(false);
    setFlashMessage(null);
  }, [initialFixture]);

  const hasAuthoringConfig = !!initialFixture?.authoring_config;
  const busy = isSubmitting;
  const transcripts = useMemo(
    () => authoringConfig.transcripts ?? [],
    [authoringConfig.transcripts]
  );
  const fixtureShape = authoringConfig.fixture_shape ?? inferFixtureShape(authoringConfig);
  const advancedInputs = useMemo(() => advancedInputSummary(authoringConfig), [authoringConfig]);
  const templateVariablesState = useMemo(
    () => parsedTemplateVariables(templateVariablesText),
    [templateVariablesText]
  );
  const hasDraftContent =
    transcripts.length > 0 ||
    !!authoringConfig.example_context?.display_label?.trim() ||
    !!authoringConfig.example_context?.notes?.trim() ||
    normalizedIdentityKeys(authoringConfig).length > 0 ||
    Object.keys(templateVariablesState.value).length > 0 ||
    !!authoringConfig.anonymization.notes?.trim();
  const effectiveReviewRuntime = hasDraftContent
    ? draftRuntimeSummary(authoringConfig, templateVariablesState.value)
    : existingRuntimePreview;
  const missingSessionDateCount = transcripts.filter(
    (transcript) => !transcript.session_date?.trim()
  ).length;
  const hasRequiredFixtureName = mode === 'version' || name.trim().length > 0;
  const hasAtLeastOneTranscript = transcripts.length > 0;
  const canSubmit =
    hasRequiredFixtureName && hasAtLeastOneTranscript && templateVariablesState.isValid;

  const showFlashMessage = (message: string, severity: FlashMessage['severity'] = 'info') => {
    setFlashMessage({ message, severity });
  };

  const setFixtureShape = (nextShape: FixtureShape) => {
    setAuthoringConfig((current) => ({
      ...current,
      fixture_shape: nextShape,
    }));
  };

  const updateTranscript = (index: number, patch: Partial<FixtureTranscriptItem>) => {
    setAuthoringConfig((current) => ({
      ...current,
      transcripts: current.transcripts.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      ),
    }));
  };

  const removeTranscript = (index: number) => {
    setAuthoringConfig((current) => {
      const nextTranscripts = current.transcripts.filter((_, itemIndex) => itemIndex !== index);
      const nextPrimary =
        current.primary_transcript_id &&
        nextTranscripts.some((item) => item.id === current.primary_transcript_id)
          ? current.primary_transcript_id
          : (defaultPrimaryTranscriptID(
              nextTranscripts,
              undefined,
              current.expose_primary_as_transcript
            ) ?? '');
      return {
        ...current,
        primary_transcript_id: nextPrimary,
        transcripts: nextTranscripts,
      };
    });
  };

  const addTranscripts = (items: FixtureTranscriptItem[]) => {
    setAuthoringConfig((current) => {
      const normalized = normalizeAddedTranscripts(current.transcripts, items);
      const nextTranscripts = [...current.transcripts, ...normalized];
      return {
        ...current,
        fixture_shape:
          nextTranscripts.length > 1
            ? 'transcript_bundle'
            : (current.fixture_shape ?? 'single_transcript'),
        primary_transcript_id:
          defaultPrimaryTranscriptID(
            nextTranscripts,
            current.primary_transcript_id,
            current.expose_primary_as_transcript
          ) ?? '',
        transcripts: nextTranscripts,
      };
    });
    showFlashMessage(
      `Added ${items.length} transcript${items.length === 1 ? '' : 's'}. Review the transcript cards below and fill in any missing session dates.`,
      'success'
    );
  };

  const addManualTranscript = () => {
    const body = draftTranscriptBody.trim();
    if (!body) {
      showFlashMessage('Transcript body is required to add a pasted transcript.', 'error');
      return;
    }

    addTranscripts([
      {
        id:
          inferTranscriptId(draftTranscriptTitle) ||
          `transcript-${authoringConfig.transcripts.length + 1}`,
        title:
          draftTranscriptTitle.trim() || `Transcript ${authoringConfig.transcripts.length + 1}`,
        session_date: draftTranscriptSessionDate.trim() || undefined,
        markdown: draftTranscriptFormat === 'markdown' ? body : undefined,
        content: draftTranscriptFormat === 'text' ? body : undefined,
        mime_type:
          draftTranscriptFormat === 'markdown'
            ? 'application/vnd.google-apps.document'
            : 'text/plain',
      },
    ]);
    setDraftTranscriptTitle('');
    setDraftTranscriptSessionDate('');
    setDraftTranscriptBody('');
    showFlashMessage(
      'Added transcript from pasted text. Review the transcript card below and fill in the session date if needed.',
      'success'
    );
  };

  const buildDraft = (): FixtureDraft | null => {
    try {
      const templateVariables = templateVariablesState.value;
      const normalizedTranscripts = authoringConfig.transcripts.map((transcript) => ({
        ...transcript,
        id: transcript.id.trim(),
        title: transcript.title?.trim() || undefined,
        session_date: transcript.session_date?.trim() || undefined,
        mime_type: transcript.mime_type?.trim() || undefined,
        content: transcript.content?.trim() || undefined,
        markdown: transcript.markdown?.trim() || undefined,
      }));
      const primaryTranscriptID = defaultPrimaryTranscriptID(
        normalizedTranscripts,
        authoringConfig.primary_transcript_id,
        authoringConfig.expose_primary_as_transcript
      );
      const normalized: FixtureAuthoringConfig = {
        ...authoringConfig,
        kind: 'anonymized_transcript_bundle',
        fixture_shape:
          normalizedTranscripts.length > 1 ? 'transcript_bundle' : authoringConfig.fixture_shape,
        example_context: {
          display_label: authoringConfig.example_context?.display_label?.trim() || undefined,
          notes: authoringConfig.example_context?.notes?.trim() || undefined,
        },
        identity_defaults: {
          person_id: authoringConfig.identity_defaults?.person_id?.trim() || undefined,
          org_id: authoringConfig.identity_defaults?.org_id?.trim() || undefined,
          draft_bundle_id: authoringConfig.identity_defaults?.draft_bundle_id?.trim() || undefined,
        },
        template_variables: templateVariables,
        primary_transcript_id: primaryTranscriptID,
        anonymization: {
          status: authoringConfig.anonymization.status.trim() || 'manual_reviewed',
          notes: authoringConfig.anonymization.notes?.trim() || undefined,
        },
        transcripts: normalizedTranscripts,
      };
      return {
        name: name.trim(),
        description: description.trim(),
        authoringConfig: normalized,
      };
    } catch {
      showFlashMessage('Template variables must be valid JSON.', 'error');
      return null;
    }
  };

  const handleSubmit = async (intent: SaveIntent) => {
    if (!templateVariablesState.isValid) {
      showFlashMessage('Template variables must be valid JSON.', 'error');
      return;
    }
    const draft = buildDraft();
    if (!draft) {
      return;
    }
    if (mode === 'create' && !draft.name) {
      showFlashMessage('Fixture set name is required.', 'error');
      return;
    }
    try {
      setFlashMessage(null);
      await onSubmit(draft, intent);
    } catch (error: any) {
      showFlashMessage(
        error?.data?.error ?? error?.message ?? 'Failed to save fixture set.',
        'error'
      );
    }
  };

  return (
    <Stack spacing={2.5}>
      <Snackbar
        open={flashMessage != null}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={(_, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setFlashMessage(null);
        }}
      >
        <Alert
          severity={flashMessage?.severity ?? 'info'}
          onClose={() => setFlashMessage(null)}
          variant="filled"
          sx={{ width: '100%', maxWidth: 560, boxShadow: 6 }}
        >
          {flashMessage?.message ?? ''}
        </Alert>
      </Snackbar>

      {mode === 'version' && !hasAuthoringConfig && (
        <Alert severity="warning">
          This fixture row has no stored authoring config. You can inspect its runtime preview, but
          creating a new version starts from a fresh guided form.
        </Alert>
      )}

      <Alert severity="success">
        This authoring flow assumes the transcripts you add are already anonymized. The backend
        compiler will keep generating the same runtime fixture shape evals use today.
      </Alert>

      <Alert severity="info">
        Create a fixture by uploading transcript files or pasting transcript text below. Required
        fields are marked *.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6">
            {mode === 'create' ? 'Fixture Details' : 'New Fixture Version'}
          </Typography>
          <TextField
            label="Fixture Set Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={mode === 'version'}
            required={mode === 'create'}
            helperText={
              mode === 'version'
                ? 'Versions inherit the existing fixture family name.'
                : 'Required. Use a stable name you will recognize when attaching this fixture to cases.'
            }
          />
          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={2}
            maxRows={FIXTURE_TEXTAREA_MAX_ROWS.description}
            helperText="Optional note about what makes this example useful."
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6">Fixture Shape</Typography>
          <Typography variant="body2" color="text.secondary">
            Fixtures stay reusable across profiles. Start by choosing whether this example is one
            transcript or a bundle of related transcripts.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant={fixtureShape === 'single_transcript' ? 'contained' : 'outlined'}
              onClick={() => setFixtureShape('single_transcript')}
              disabled={busy}
            >
              Single Transcript
            </Button>
            <Button
              variant={fixtureShape === 'transcript_bundle' ? 'contained' : 'outlined'}
              onClick={() => setFixtureShape('transcript_bundle')}
              disabled={busy}
            >
              Transcript Bundle
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {fixtureShape === 'single_transcript'
              ? 'Best for a one-session example. The first transcript becomes the primary transcript automatically.'
              : 'Best for a multi-session or multi-document example. You can optionally choose a main transcript in advanced options.'}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Transcripts</Typography>
          <Typography variant="body2" color="text.secondary">
            Add the anonymized transcript material the eval should use. Choose one of the two entry
            paths below.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Option 1: Upload transcript files</Typography>
              <Typography variant="body2" color="text.secondary">
                Use this when you already have anonymized `.md` or `.txt` transcript files ready to
                attach.
              </Typography>
              <EvalFixtureTranscriptDropzone
                disabled={busy}
                onAddTranscripts={addTranscripts}
                onError={(message) => showFlashMessage(message, 'error')}
              />
            </Stack>
          </Paper>
          <Divider />
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Option 2: Paste transcript manually</Typography>
              <Typography variant="body2" color="text.secondary">
                Use this when you want to author or paste a single transcript directly in the UI.
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Title"
                  value={draftTranscriptTitle}
                  onChange={(event) => setDraftTranscriptTitle(event.target.value)}
                  helperText="Optional. Defaults to a generated transcript name if left blank."
                  fullWidth
                />
                <TextField
                  label="Session Date"
                  value={draftTranscriptSessionDate}
                  onChange={(event) => setDraftTranscriptSessionDate(event.target.value)}
                  placeholder="2026-04-16"
                  helperText="Optional."
                  fullWidth
                />
              </Stack>
              <TextField
                label={draftTranscriptFormat === 'markdown' ? 'Markdown Body' : 'Plain Text Body'}
                value={draftTranscriptBody}
                onChange={(event) => setDraftTranscriptBody(event.target.value)}
                required
                helperText="Required when pasting a transcript manually."
                multiline
                minRows={5}
                maxRows={FIXTURE_TEXTAREA_MAX_ROWS.draftTranscriptBody}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant={draftTranscriptFormat === 'markdown' ? 'contained' : 'outlined'}
                  onClick={() => setDraftTranscriptFormat('markdown')}
                >
                  Markdown
                </Button>
                <Button
                  variant={draftTranscriptFormat === 'text' ? 'contained' : 'outlined'}
                  onClick={() => setDraftTranscriptFormat('text')}
                >
                  Plain Text
                </Button>
                <Box flexGrow={1} />
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={addManualTranscript}
                  disabled={busy}
                >
                  Add Transcript
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Divider />

          <Stack spacing={1.5}>
            {transcripts.length === 0 ? (
              <Alert severity="info">
                Add at least one anonymized transcript to build a fixture set.
              </Alert>
            ) : (
              transcripts.map((transcript, index) => (
                <Paper key={`${transcript.id}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                        {transcript.title?.trim() || `Transcript ${index + 1}`}
                      </Typography>
                      <Chip
                        size="small"
                        label={`Document ID: ${transcript.id}`}
                        variant="outlined"
                      />
                      <IconButton
                        aria-label={`remove transcript ${transcript.id}`}
                        onClick={() => removeTranscript(index)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        label="Title"
                        value={transcript.title ?? ''}
                        onChange={(event) => updateTranscript(index, { title: event.target.value })}
                        fullWidth
                      />
                      <TextField
                        label="Session Date"
                        value={transcript.session_date ?? ''}
                        onChange={(event) =>
                          updateTranscript(index, { session_date: event.target.value })
                        }
                        fullWidth
                      />
                    </Stack>
                    <TextField
                      label={transcript.markdown != null ? 'Markdown' : 'Content'}
                      value={transcriptBody(transcript)}
                      onChange={(event) =>
                        updateTranscript(index, {
                          markdown: transcript.markdown != null ? event.target.value : undefined,
                          content: transcript.markdown == null ? event.target.value : undefined,
                        })
                      }
                      multiline
                      minRows={4}
                      maxRows={FIXTURE_TEXTAREA_MAX_ROWS.transcriptBody}
                    />
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Example Context</Typography>
          <Typography variant="body2" color="text.secondary">
            Only fill out what is unique to this example. Leave raw system IDs in advanced options
            unless you know you need them.
          </Typography>
          <TextField
            label="Example Person or Scenario"
            value={authoringConfig.example_context?.display_label ?? ''}
            onChange={(event) =>
              setAuthoringConfig((current) => ({
                ...current,
                example_context: {
                  ...(current.example_context ?? {}),
                  display_label: event.target.value,
                },
              }))
            }
            helperText="Optional short label that helps future authors understand what this example represents."
          />
          <TextField
            label="Notes About This Example"
            value={authoringConfig.example_context?.notes ?? ''}
            onChange={(event) =>
              setAuthoringConfig((current) => ({
                ...current,
                example_context: {
                  ...(current.example_context ?? {}),
                  notes: event.target.value,
                },
              }))
            }
            multiline
            minRows={3}
            maxRows={FIXTURE_TEXTAREA_MAX_ROWS.exampleNotes}
            helperText="Optional notes that are useful to humans but do not need to appear in the transcript text itself."
          />
        </Stack>
      </Paper>

      <Accordion
        expanded={showAdvancedOptions}
        onChange={(_, expanded) => setShowAdvancedOptions(expanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack spacing={0.5}>
            <Typography variant="h6">Advanced Options</Typography>
            <Typography variant="body2" color="text.secondary">
              IDs, compatibility switches, and raw template data for power users.
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Person ID"
                value={authoringConfig.identity_defaults?.person_id ?? ''}
                onChange={(event) =>
                  setAuthoringConfig((current) => ({
                    ...current,
                    identity_defaults: {
                      ...(current.identity_defaults ?? {}),
                      person_id: event.target.value,
                    },
                  }))
                }
                fullWidth
              />
              <TextField
                label="Org ID"
                value={authoringConfig.identity_defaults?.org_id ?? ''}
                onChange={(event) =>
                  setAuthoringConfig((current) => ({
                    ...current,
                    identity_defaults: {
                      ...(current.identity_defaults ?? {}),
                      org_id: event.target.value,
                    },
                  }))
                }
                fullWidth
              />
              <TextField
                label="Draft Bundle ID"
                value={authoringConfig.identity_defaults?.draft_bundle_id ?? ''}
                onChange={(event) =>
                  setAuthoringConfig((current) => ({
                    ...current,
                    identity_defaults: {
                      ...(current.identity_defaults ?? {}),
                      draft_bundle_id: event.target.value,
                    },
                  }))
                }
                fullWidth
              />
            </Stack>

            <TextField
              label="Template Variables JSON"
              value={templateVariablesText}
              onChange={(event) => setTemplateVariablesText(event.target.value)}
              error={!templateVariablesState.isValid}
              multiline
              minRows={4}
              maxRows={FIXTURE_TEXTAREA_MAX_ROWS.templateVariables}
              helperText={
                templateVariablesState.isValid
                  ? 'Optional JSON object merged into template variables for profiles that need extra runtime data.'
                  : 'Enter a valid JSON object before saving.'
              }
            />

            {transcripts.length > 1 && (
              <TextField
                select
                label="Primary Transcript"
                value={authoringConfig.primary_transcript_id ?? ''}
                onChange={(event) =>
                  setAuthoringConfig((current) => ({
                    ...current,
                    primary_transcript_id: event.target.value,
                  }))
                }
                helperText="Only needed if you want to mark one transcript as the main reference for compatibility mode."
              >
                <MenuItem value="">
                  <em>Auto-select if needed</em>
                </MenuItem>
                {transcripts.map((transcript) => (
                  <MenuItem key={transcript.id} value={transcript.id}>
                    {transcript.title ?? transcript.id}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={authoringConfig.expose_primary_as_transcript ?? false}
                  onChange={(event) =>
                    setAuthoringConfig((current) => ({
                      ...current,
                      expose_primary_as_transcript: event.target.checked,
                    }))
                  }
                />
              }
              label="Compatibility mode: also inject the primary transcript as document_context.transcript"
            />

            <TextField
              label="Anonymization Notes"
              value={authoringConfig.anonymization.notes ?? ''}
              onChange={(event) =>
                setAuthoringConfig((current) => ({
                  ...current,
                  anonymization: {
                    ...current.anonymization,
                    notes: event.target.value,
                  },
                }))
              }
              multiline
              minRows={2}
              maxRows={FIXTURE_TEXTAREA_MAX_ROWS.anonymizationNotes}
              helperText="Optional internal note about how the transcript bundle was anonymized."
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6">Review</Typography>
          <Typography variant="body2" color="text.secondary">
            Confirm the compiled fixture summary, then save from the action bar at the bottom of
            this section.
          </Typography>

          {!effectiveReviewRuntime ? (
            <Alert severity="info">
              Save runs backend compile-time validation. The review below updates from your current
              draft when you add transcripts or edit fixture context.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                This fixture will provide {effectiveReviewRuntime.summary.transcript_count}{' '}
                {effectiveReviewRuntime.summary.transcript_count === 1
                  ? 'transcript'
                  : 'transcripts'}{' '}
                as document-backed context.
              </Typography>
              {!templateVariablesState.isValid && (
                <Alert severity="warning">
                  Template Variables JSON is invalid. Save will be blocked until you fix it.
                </Alert>
              )}
              {missingSessionDateCount > 0 && (
                <Alert severity="info">
                  {missingSessionDateCount}{' '}
                  {missingSessionDateCount === 1 ? 'transcript is' : 'transcripts are'} missing a
                  session date.
                </Alert>
              )}
              {!hasDraftContent && existingRuntimePreview && (
                <Alert severity="info">
                  Showing the saved runtime summary for this existing fixture version. Any edits you
                  make above will update this review immediately.
                </Alert>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Fixture Shape"
                  value={
                    fixtureShape === 'single_transcript' ? 'Single transcript' : 'Transcript bundle'
                  }
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Transcript Count"
                  value={String(effectiveReviewRuntime.summary.transcript_count)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Example Context"
                  value={
                    authoringConfig.example_context?.display_label?.trim()
                      ? authoringConfig.example_context.display_label
                      : 'None'
                  }
                  InputProps={{ readOnly: true }}
                />
              </Stack>
              <TextField
                label="Document IDs Available To The Eval"
                value={effectiveReviewRuntime.summary.document_ids.join(', ')}
                InputProps={{ readOnly: true }}
                fullWidth
              />
              {advancedInputs.length > 0 ? (
                <Alert severity="info">Advanced inputs enabled: {advancedInputs.join(', ')}.</Alert>
              ) : (
                <Alert severity="success">
                  No advanced runtime inputs are enabled. This fixture is transcript-backed only.
                </Alert>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showDebug}
                    onChange={(event) => setShowDebug(event.target.checked)}
                    icon={<BugReportIcon />}
                    checkedIcon={<BugReportIcon />}
                  />
                }
                label="Show technical details"
              />
              {showDebug && (
                <Stack spacing={1.5}>
                  <TextField
                    label="Primary Transcript ID"
                    value={effectiveReviewRuntime.summary.primary_transcript_id ?? ''}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Compiled Identity Keys"
                    value={effectiveReviewRuntime.summary.identity_keys.join(', ')}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Compiled Template Variable Keys"
                    value={effectiveReviewRuntime.summary.template_variable_keys.join(', ')}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label={hasDraftContent ? 'Current Draft JSON' : 'Saved Runtime JSON'}
                    value={JSON.stringify(
                      hasDraftContent ? authoringConfig : effectiveReviewRuntime,
                      null,
                      2
                    )}
                    multiline
                    minRows={12}
                    maxRows={FIXTURE_TEXTAREA_MAX_ROWS.compiledRuntimeJSON}
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
              )}
            </>
          )}
          {!hasAtLeastOneTranscript && (
            <Alert severity="warning">
              Add at least one transcript before saving this fixture.
            </Alert>
          )}
          <Divider />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="flex-end"
          >
            {caseFlowActionLabel && (
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={() => void handleSubmit('use_in_case')}
                disabled={busy || !canSubmit}
              >
                {isSubmitting ? 'Saving…' : caseFlowActionLabel}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => void handleSubmit('stay')}
              disabled={busy || !canSubmit}
            >
              {isSubmitting ? 'Saving…' : mode === 'create' ? 'Save Fixture' : 'Save Version'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
